import React, { useState } from 'react';

interface URLInspectorPanelProps {
  siteUrl: string;
}

interface InspectionResult {
  inspectionResult?: {
    indexStatusResult?: {
      verdict?: string;
      coverageState?: string;
      robotsTxtState?: string;
      indexingState?: string;
      lastCrawlTime?: string;
      pageFetchState?: string;
      googleCanonical?: string;
      userCanonical?: string;
    };
    mobileUsabilityResult?: {
      verdict?: string;
      issues?: Array<{
        issueType?: string;
        severity?: string;
        message?: string;
      }>;
    };
    richResultsResult?: {
      verdict?: string;
      detectedItems?: Array<{
        richResultType?: string;
        items?: Array<{ name?: string }>;
      }>;
    };
    ampResult?: {
      verdict?: string;
      ampUrl?: string;
      issues?: Array<{
        issueMessage?: string;
        severity?: string;
      }>;
    };
  };
}

const URLInspectorPanel: React.FC<URLInspectorPanelProps> = ({ siteUrl }) => {
  const [urlToInspect, setUrlToInspect] = useState('');
  const [inspectionData, setInspectionData] = useState<InspectionResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleInspect = async () => {
    if (!urlToInspect.trim()) {
      setError('Please enter a URL to inspect');
      return;
    }

    setIsLoading(true);
    setError(null);
    setInspectionData(null);

    try {
      const response = await fetch('/api/gsc/url-inspection', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          siteUrl,
          inspectionUrl: urlToInspect.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to inspect URL');
      }

      const data = await response.json();
      setInspectionData(data);
    } catch (err: any) {
      setError(err.message || 'Failed to inspect URL');
    } finally {
      setIsLoading(false);
    }
  };

  const getVerdictBadge = (verdict?: string) => {
    if (!verdict) return null;
    
    const verdictLower = verdict.toLowerCase();
    if (verdictLower.includes('pass') || verdictLower.includes('success')) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">✅ {verdict}</span>;
    }
    if (verdictLower.includes('fail') || verdictLower.includes('error')) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">❌ {verdict}</span>;
    }
    if (verdictLower.includes('warning') || verdictLower.includes('partial')) {
      return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">⚠️ {verdict}</span>;
    }
    return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">{verdict}</span>;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
          <span className="text-2xl">🔍</span>
          URL Inspector
        </h2>
        <p className="text-sm text-gray-600 mt-1">
          Inspect any URL to see index status, mobile usability, structured data, and more
        </p>
      </div>

      {/* URL Input */}
      <div className="mb-6">
        <div className="flex gap-2">
          <input
            type="url"
            value={urlToInspect}
            onChange={(e) => setUrlToInspect(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleInspect()}
            placeholder="https://example.com/page-to-inspect"
            className="flex-1 border border-gray-300 rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            onClick={handleInspect}
            disabled={isLoading}
            className="bg-gradient-to-r from-orange-400 to-pink-600 hover:from-orange-500 hover:to-pink-700 text-white px-6 py-2 rounded-md font-medium transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? 'Inspecting...' : 'Inspect URL'}
          </button>
        </div>
        {error && (
          <div className="mt-2 text-sm text-red-600">
            {error}
          </div>
        )}
      </div>

      {/* Inspection Results */}
      {isLoading && (
        <div className="text-center py-8">
          <div className="inline-flex items-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md text-gray-500 bg-gray-100 transition ease-in-out duration-150">
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Inspecting URL...
          </div>
        </div>
      )}

      {inspectionData && inspectionData.inspectionResult && (
        <div className="space-y-4">
          {/* Index Status */}
          {inspectionData.inspectionResult.indexStatusResult && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                📑 Index Status
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Verdict:</span>
                  {getVerdictBadge(inspectionData.inspectionResult.indexStatusResult.verdict)}
                </div>
                {inspectionData.inspectionResult.indexStatusResult.coverageState && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Coverage State:</span>
                    <span className="text-sm font-medium">{inspectionData.inspectionResult.indexStatusResult.coverageState}</span>
                  </div>
                )}
                {inspectionData.inspectionResult.indexStatusResult.indexingState && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Indexing State:</span>
                    <span className="text-sm font-medium">{inspectionData.inspectionResult.indexStatusResult.indexingState}</span>
                  </div>
                )}
                {inspectionData.inspectionResult.indexStatusResult.robotsTxtState && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Robots.txt:</span>
                    <span className="text-sm font-medium">{inspectionData.inspectionResult.indexStatusResult.robotsTxtState}</span>
                  </div>
                )}
                {inspectionData.inspectionResult.indexStatusResult.lastCrawlTime && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">Last Crawled:</span>
                    <span className="text-sm font-medium">{new Date(inspectionData.inspectionResult.indexStatusResult.lastCrawlTime).toLocaleString()}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Mobile Usability */}
          {inspectionData.inspectionResult.mobileUsabilityResult && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                📱 Mobile Usability
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Verdict:</span>
                  {getVerdictBadge(inspectionData.inspectionResult.mobileUsabilityResult.verdict)}
                </div>
                {inspectionData.inspectionResult.mobileUsabilityResult.issues && inspectionData.inspectionResult.mobileUsabilityResult.issues.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Issues:</p>
                    <ul className="space-y-1">
                      {inspectionData.inspectionResult.mobileUsabilityResult.issues.map((issue, idx) => (
                        <li key={idx} className="text-sm text-red-600 flex items-start gap-2">
                          <span>•</span>
                          <span>{issue.message || issue.issueType}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Rich Results / Structured Data */}
          {inspectionData.inspectionResult.richResultsResult && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                ✨ Rich Results / Structured Data
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Verdict:</span>
                  {getVerdictBadge(inspectionData.inspectionResult.richResultsResult.verdict)}
                </div>
                {inspectionData.inspectionResult.richResultsResult.detectedItems && inspectionData.inspectionResult.richResultsResult.detectedItems.length > 0 && (
                  <div className="mt-3">
                    <p className="text-sm font-medium text-gray-700 mb-2">Detected Schema Types:</p>
                    <div className="flex flex-wrap gap-2">
                      {inspectionData.inspectionResult.richResultsResult.detectedItems.map((item, idx) => (
                        <span key={idx} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {item.richResultType}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* AMP Status */}
          {inspectionData.inspectionResult.ampResult && (
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
                ⚡ AMP Status
              </h3>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Verdict:</span>
                  {getVerdictBadge(inspectionData.inspectionResult.ampResult.verdict)}
                </div>
                {inspectionData.inspectionResult.ampResult.ampUrl && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-gray-600">AMP URL:</span>
                    <a href={inspectionData.inspectionResult.ampResult.ampUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline truncate max-w-md">
                      {inspectionData.inspectionResult.ampResult.ampUrl}
                    </a>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {!isLoading && !inspectionData && !error && (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <span className="text-4xl mb-4 block">🔍</span>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ready to inspect</h3>
          <p className="text-sm text-gray-500">
            Enter a URL from your site above to see detailed inspection results
          </p>
        </div>
      )}
    </div>
  );
};

export default URLInspectorPanel;
